import { useMemo, useState } from 'react'
import Link from 'next/link'
import { RiArrowRightLine } from '@remixicon/react'
import { Banner } from './banner'
import { Search } from './search'
import { Wrapper } from './wrapper'
import { IntegrationItem } from './integrationItem'
import styles from './integrations.module.css'

const searchAliases: Record<string, string> = {
  'Google Sheet': 'Google Sheets',
  Integromat: 'Make'
}

export interface IntegrationGroup {
  category: string
  apps: AppModel[]
}
export function Directory({
  categories,
  groups,
  selectedCategory
}: {
  categories: string[]
  groups: IntegrationGroup[]
  selectedCategory?: string
}) {
  const [query, setQuery] = useState('')
  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase()
    return groups
      .map(group => ({
        ...group,
        apps: group.apps.filter(
          app =>
            !keyword ||
            `${app.Title} ${app.Overview} ${searchAliases[app.Title] || ''}`
              .toLowerCase()
              .includes(keyword)
        )
      }))
      .filter(group => group.apps.length > 0)
  }, [groups, query])
  const total = filtered.reduce((sum, group) => sum + group.apps.length, 0)
  return (
    <main className={styles.page}>
      <Banner category={selectedCategory}>
        <Search value={query} onChange={setQuery} />
      </Banner>
      <Wrapper categories={categories} selectedCategory={selectedCategory}>
        <p className={styles.resultSummary} role="status" aria-live="polite">
          {query.trim()
            ? `${total} ${total === 1 ? 'integration' : 'integrations'} found for “${query.trim()}”`
            : `${total} ${total === 1 ? 'integration' : 'integrations'} to connect your workflow`}
        </p>
        {filtered.length ? (
          filtered.map(group => (
            <IntegrationItem
              key={group.category}
              heading={group.category}
              apps={group.apps}
            />
          ))
        ) : (
          <div className={styles.empty}>
            <h2>No matching integrations</h2>
            <p>Try another app name or a broader keyword.</p>
            <button
              type="button"
              className={styles.textLink}
              onClick={() => setQuery('')}
            >
              Clear search
              <RiArrowRightLine aria-hidden="true" />
            </button>
          </div>
        )}
      </Wrapper>
      <section className={styles.help} aria-labelledby="integration-help-title">
        <div className={styles.container}>
          <div>
            <h2 id="integration-help-title">Have a workflow in mind?</h2>
            <p>Tell us which tools you use and what you want to connect.</p>
          </div>
          <Link href="/contact" className={styles.textLink}>
            Talk to us
            <RiArrowRightLine aria-hidden="true" />
          </Link>
        </div>
      </section>
    </main>
  )
}
